<template>
  <div class="patent-analysis">
    <div class="analysis-header">
      <h3 v-if="isLoading">专利分析</h3>
      <h3 v-else>专利分析结果</h3>
      <div class="header-actions">
        <el-button type="primary" @click="resetAnalysis">重新分析</el-button>
      </div>
    </div>
    
    <div class="analysis-content">
      <!-- 加载状态 -->
      <div class="loading-section" v-if="isLoading">
        <el-icon class="loading-icon"><Loading /></el-icon>
        <div class="loading-text">正在获取专利PDF并分析...</div>
      </div>
      
      <!-- 错误状态 -->
      <div class="error-section" v-else-if="errorMessage">
        <el-alert
          :title="errorMessage"
          type="error"
          show-icon
          :closable="false"
        />
        <el-button type="primary" @click="fetchAndAnalyzePatent">重试</el-button>
      </div>
      
      <!-- 分析结果 -->
      <div class="result-section" v-else-if="analysisResult">  
        <!-- 分析内容 -->
        <div class="analysis-result">
          <div class="result-item">
            <h5>专利解读分析</h5>
            <div class="result-content">{{ analysisResult.patentAnalysis || analysisResult.analysis || '' }}</div>
          </div>
          
          <div class="result-item">
            <h5>问题解读分析</h5>
            <div class="result-content">{{ analysisResult.problemAnalysis || '' }}</div>
          </div>
          
          <div class="result-item">
            <h5>优点分析</h5>
            <div class="result-content result-content--pros">{{ toTextBlock(analysisResult.pros) }}</div>
          </div>
          
          <div class="result-item">
            <h5>缺点分析</h5>
            <div class="result-content result-content--cons">{{ toTextBlock(analysisResult.cons) }}</div>
          </div>
          
          <div class="result-item">
            <h5>建议</h5>
            <div class="result-content">{{ toTextBlock(analysisResult.suggestions) }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { Loading } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { patentPdfSearch } from '@/api/process'
import { savePatentInterpretation } from '@/api/persistence'
// 路由参数
const route = useRoute()
// 专利ID（公开号）
const patentId = ref('')
// 专利数据库ID
const patentDbId = ref('')
// 问题描述
const problem = ref('')
// 项目ID（解读落库用）
const projectId = ref('')
// 分析结果
const analysisResult = ref(null)
// 加载状态
const isLoading = ref(true)
// 错误信息
const errorMessage = ref('')
const pdfSource = ref('')
const patentName = ref('')

// 优点/缺点/建议：模型返回的是字符串数组，这里拼成多行文本按文本展示（不再渲染成标签）
const toTextBlock = (v) => {
  if (Array.isArray(v)) {
    return v
      .map(item => (item === null || item === undefined ? '' : String(item)))
      .map(s => s.trim())
      .filter(Boolean)
      .join('\n')
  }
  return v === null || v === undefined ? '' : String(v)
}

// 解读结果落库（静默）：5 块内容合成完整 JSON 存 external_patents.ai_interpretation（专利级共享）
const persistInterpretation = async (result) => {
  if (!patentId.value || !result) return
  try {
    // 5 块内容：专利解读分析 / 问题解读分析 / 优点 / 缺点 / 建议
    const content = JSON.stringify({
      patentAnalysis: result.patentAnalysis || result.analysis || '',
      problemAnalysis: result.problemAnalysis || '',
      pros: Array.isArray(result.pros) ? result.pros : [],
      cons: Array.isArray(result.cons) ? result.cons : [],
      suggestions: Array.isArray(result.suggestions) ? result.suggestions : []
    })
    // 专利数据库ID：跳转可能没带（专利未落库），先按公开号确保落库并拿回ID
    let dbId = patentDbId.value ? Number(patentDbId.value) : null
    if (!dbId && patentId.value) {
      // 用公开号最小信息落库（标题先占位，加入库时补全）
      const { saveOrGetByExternalId } = await import('@/api/persistence')
      dbId = await saveOrGetByExternalId(patentId.value)
    }
    if (!dbId) {
      console.warn('解读落库跳过：无法确定专利数据库ID')
      return
    }
    await savePatentInterpretation(dbId, content)
    console.log('专利解读已落库:', dbId)
  } catch (e) {
    console.error('专利解读落库失败（静默）:', e)
  }
}
// 获取专利PDF并分析
const fetchAndAnalyzePatent = async () => {
  isLoading.value = true
  errorMessage.value = ''
  
  try {
    // 获取专利ID和问题描述
    patentId.value = route.query.id || ''
    patentDbId.value = route.query.dbId || ''
    problem.value = route.query.problemDescription || ''
    projectId.value = route.query.projectId || ''
    
    if (!patentId.value) {
      throw new Error('专利ID不能为空')
    }
    
    if (!problem.value) {
      throw new Error('问题描述不能为空')
    }
    
    // 获取专利PDF
    console.log('获取专利PDF，专利ID:', patentId.value)
    const pdfResponse = await patentPdfSearch({ pn: patentId.value })
    
    if (!pdfResponse) {
      throw new Error('获取专利PDF失败')
    }
    
     // 将文件流转换为Blob对象
        const uint8Array = new Uint8Array(pdfResponse)
        patentName.value = patentId.value + '专利.pdf'
        //const blob = new Blob([uint8Array], patentName.value,{ type: 'application/pdf' })
        const file = new File([uint8Array], patentName.value, { type: 'application/pdf' });
    analyze(file).catch(err => {
      console.error('专利分析未捕获异常:', err)
      errorMessage.value = '专利分析失败：' + (err.message || '请重试')
      isLoading.value = false
    })
    
    
  } catch (error) {
    console.error('专利分析失败:', error)
    errorMessage.value = '专利分析失败：' + error.message
    ElMessage.error(errorMessage.value)
  } finally {
    
  }
}

// 重新分析：清空当前结果后重新拉取 PDF 并重新分析
// 注意：这一步会真实重新请求专利 PDF（计费）并再次调用模型/OCR（计费），不是单纯清空界面
const resetAnalysis = () => {
  analysisResult.value = null
  fetchAndAnalyzePatent()
}
const analyze = async (file) => {
    // 分析专利
    console.log('开始分析专利', file)
    isLoading.value = true
    try {
      const formData = new FormData()
      formData.append('patent', file)
      formData.append('problem', problem.value)
      
      const analysisResponse = await fetch('/api/patent/analyze', {
        method: 'POST',
        body: formData
      })
      
      if (!analysisResponse.ok) {
        // 把后端返回的具体原因读出来显示，别只报一个状态码（否则用户完全不知道该重试还是换专利）
        let reason = `分析失败，状态码：${analysisResponse.status}`
        try {
          const errBody = await analysisResponse.json()
          if (errBody && errBody.error) reason = errBody.error
        } catch (parseErr) {
          // 响应体不是 JSON，保留状态码提示
        }
        throw new Error(reason)
      }
      
      const result = await analysisResponse.json()
      analysisResult.value = result
      
      // 解读结果落库（静默）：5 块内容全存
      persistInterpretation(result)
      
      ElMessage.success('专利分析完成')
      isLoading.value = false
    } catch (e) {
      console.error('专利分析异常:', e)
      errorMessage.value = '专利分析失败：' + (e.message || '请重试')
      isLoading.value = false
      throw e
    }
}


// 组件挂载时获取专利并分析
onMounted(() => {
  fetchAndAnalyzePatent()
})
</script>

<style scoped lang="scss">
.patent-analysis {
  background-color: #ffffff;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  margin: 20px 0;
  width: calc(100% - 40px);
}
.loading-section {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
}

.analysis-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e8e8e8;
  
  h3 {
    font-size: 18px;
    font-weight: 600;
    margin: 0;
    color: #333;
  }
}

.analysis-content {
  min-height: 400px;
  width: calc(100% - 40px);
}

/* 上传区域 */
.upload-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.patent-uploader {
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  padding: 40px 20px;
  text-align: center;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #1890ff;
  }
}

.problem-section {
  h4 {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 10px;
    color: #333;
  }
}

.problem-input {
  width: 100%;
  min-height: 120px;
}

.action-section {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

/* 结果区域 */
.result-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.result-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
  border-bottom: 1px solid #e8e8e8;
  
  h4 {
    font-size: 16px;
    font-weight: 600;
    margin: 0;
    color: #333;
  }
}

.analysis-result {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.result-item {
  background-color: #fafafa;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e8e8e8;
  
  h5 {
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 12px;
    color: #333;
  }
}

.result-content {
  font-size: 14px;
  line-height: 1.6;
  color: #666;
  white-space: pre-wrap;
}

/* 优点标绿、缺点标红（沿用原标签的语义色，但内容按文本展示） */
.result-content--pros {
  color: #67c23a;
}

.result-content--cons {
  color: #f56c6c;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .patent-analysis {
    padding: 16px;
  }
  
  .analysis-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .result-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}
</style>
